/**
 * Pure edit + validation helpers for per-parameter operating-mode activation (W4).
 *
 * `active_modes` on a ParameterDef declares which operating modes that parameter
 * is measured in (the simulator emits its tag only in those modes; ABSENT or
 * EMPTY = active in every mode). These helpers edit it immutably and cascade so it
 * stays consistent with the mode list the operating-mode editor mutates alongside
 * it: renaming a mode rewrites the parameters that referenced it, removing a mode
 * strips it. Validation mirrors the FSM rule -- every active mode must be a
 * declared mode.
 */

import type { ParameterDef } from "@/foundry-widgets/foundry-plant";

/** A parameter's declared active modes (absent/empty = active in every mode). */
export function paramActiveModes(p: ParameterDef): string[] {
  return p.active_modes ?? [];
}

/** Toggle a mode in parameters[index].active_modes (empty = active in every mode). */
export function toggleParamModeAt(
  params: ParameterDef[],
  index: number,
  mode: string,
): ParameterDef[] {
  return params.map((p, i) => {
    if (i !== index) return p;
    const cur = p.active_modes ?? [];
    const next = cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode];
    return { ...p, active_modes: next };
  });
}

/** Cascade a mode rename through every parameter's active_modes. */
export function renameModeInParams(
  params: ParameterDef[],
  old: string,
  next: string,
): ParameterDef[] {
  return params.map((p) => {
    const am = p.active_modes;
    if (!am || !am.includes(old)) return p;
    return { ...p, active_modes: am.map((m) => (m === old ? next : m)) };
  });
}

/** Cascade a mode removal: strip it from every parameter's active_modes. */
export function dropModeFromParams(params: ParameterDef[], mode: string): ParameterDef[] {
  return params.map((p) => {
    const am = p.active_modes;
    if (!am || !am.includes(mode)) return p;
    return { ...p, active_modes: am.filter((m) => m !== mode) };
  });
}

/** Every active_modes entry must be a declared mode (mirrors the FSM validation). */
export function validateParamActiveModes(params: ParameterDef[], modes: string[]): string[] {
  const set = new Set(modes);
  const errs: string[] = [];
  for (const p of params) {
    for (const m of p.active_modes ?? []) {
      if (!set.has(m)) {
        errs.push(`Parameter "${p.name}": active mode "${m}" is not a declared mode.`);
      }
    }
  }
  return errs;
}
